/// <reference types="@types/google.maps" />
import { getAppConfig } from '@services/config';

import {
    GoogleMapsAddressResponse,
    GoogleMapsAutocompleteItem,
    GoogleMapsSerializedAddress,
} from '@app-types/delivery';

import { Loader } from '@googlemaps/js-api-loader';

const appConfig = getAppConfig();
class GoogleMapsAutocompleteService {
    private loader = new Loader({
        apiKey: appConfig.googleMaps.apiKey,
        version: 'weekly',
    });

    public async getAddresses(query: string): Promise<GoogleMapsAutocompleteItem[]> {
        const placesLib = await this.loader.importLibrary('places');
        const autocompleteService = new placesLib.AutocompleteService();

        return new Promise((resolve) => {
            if (!query) return resolve([]);

            autocompleteService.getPlacePredictions(
                {
                    input: query,
                    componentRestrictions: { country: 'fr' },
                    types: ['address'],
                },
                (places, status) => {
                    if (status != placesLib.PlacesServiceStatus.OK || !places) {
                        resolve([]);
                    } else {
                        const autocompleteData = places.map((p) => ({
                            data: { place_id: p.place_id },
                            label: p.description,
                        }));
                        resolve(autocompleteData);
                    }
                },
            );
        });
    }

    public async getAddressDetails(payload: google.maps.GeocoderRequest): Promise<GoogleMapsAddressResponse> {
        const geocodingLib = await this.loader.importLibrary('geocoding');
        const geocoder = new geocodingLib.Geocoder();

        return new Promise((resolve, reject) => {
            geocoder.geocode(payload, (results, status) => {
                if (status !== geocodingLib.GeocoderStatus.OK || !results?.[0]) {
                    reject(new Error('Geocoder failed due to: ' + status));
                } else {
                    const addressResponse = this.serializeAddress(results[0]);
                    resolve(addressResponse);
                }
            });
        });
    }

    private serializeAddress(geocoderResult: google.maps.GeocoderResult): GoogleMapsAddressResponse {
        const serializedAddress: GoogleMapsSerializedAddress = {
            formatted_address: geocoderResult.formatted_address,
            locality: '',
            postal_code: '',
            route: '',
            street_number: '',
        };

        const types = ['route', 'locality', 'postal_code', 'street_number'];
        for (const component of geocoderResult.address_components) {
            for (const componentType of component.types) {
                if (types.includes(componentType)) {
                    const key = componentType as keyof typeof serializedAddress;
                    serializedAddress[key] = component.long_name;
                }
            }
        }

        const address = {
            postalCode: serializedAddress.postal_code,
            city: serializedAddress.locality,
            streetName: serializedAddress.route,
            streetNumber: serializedAddress.street_number,
            isVerified: false,
        };

        return {
            address,
            formattedAddress: serializedAddress.formatted_address,
        };
    }
}

export const googleMapsAutocompleteService = new GoogleMapsAutocompleteService();
